import React from "react";
import { Header } from "../../../Components";
import TableProgressBar from "../../TableProgressBar";
import { abbreviateNumber } from "../../../utils/format-utils";

interface MaxValues {
  maxNtbRate: number;
  maxNtbRepeatRate: number;
}

const ProgressBarRenderer = (value: number, maxCompleted: number, type: string): JSX.Element => {
  return <TableProgressBar label={`${value}%`} maxCompleted={maxCompleted} value={value} />;
};
interface TableData {
  customersConverted: number;
  ntbCustomers: number;
  ntbPurchases: number;
  ntbRate: number;
  ntbRepeat: number;
  ntbRepeatRate: number;
  purchases: number;
  trackedAsin: string;
}

export const getNtbTableHeaders = (maxValues: MaxValues): Header[] => [
  {
    name: "trackedAsin",
    label: "ASIN",
  },
  {
    name: "customersConverted",
    label: "Customers Converted",
    flex: 1,
    minFlexWidth: 141,
    renderer: (data: TableData): string => abbreviateNumber(data.customersConverted),
  },
  {
    name: "purchases",
    label: "Purchases",
    flex: 1,
    minFlexWidth: 141,
    renderer: (data: TableData): string => abbreviateNumber(data.purchases),
  },
  {
    name: "ntbCustomers",
    label: "NTB Customers",
    flex: 1,
    minFlexWidth: 141,
    renderer: (data: TableData): string => abbreviateNumber(data.ntbCustomers),
  },
  {
    name: "ntbPurchases",
    label: "NTB Purchases",
    flex: 1,
    minFlexWidth: 141,
    renderer: (data: TableData): string => abbreviateNumber(data.ntbPurchases),
  },
  {
    name: "ntbRate",
    label: "NTB Rate",
    flex: 1,
    minFlexWidth: 141,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(data.ntbRate, maxValues.maxNtbRate, "ntbRate"),
  },
  {
    name: "ntbRepeat",
    label: "NTB Repeat",
    flex: 1,
    minFlexWidth: 141,
  },
  {
    name: "ntbRepeatRate",
    label: "NTB Repeat Rate",
    flex: 1,
    minFlexWidth: 141,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(data.ntbRepeatRate, maxValues.maxNtbRepeatRate, "ntbRepeatRate"),
  },
];

interface AmcAvailableFilters {
  granularity: string[];
  months?: string[];
  weeks?: string[];
}

interface NtbBehaviorByGroup {
  customersConverted: number;
  ntbCustomers: number;
  ntbPurchases: number;
  ntbRate: number;
  ntbRepeat: number;
  ntbRepeatRate: number;
  purchases: number;
  trackedAsin: string;
}

interface NtbVsRepeatBehavior {
  customers: {
    month: string;
    ntbCustomers: number;
    ntbRate: number;
    ntbRepeatCustomers: number;
    ntbRepeatRate: number;
    ntbCustomersPercent?: number;
    ntbRepeatCustomersPercent?: number;
  }[];
  purchases: {
    month: string;
    ntbCustomerRate: number;
    ntbCustomers: number;
    repeatCustomerRate: number;
    repeatCustomers: number;
    ntbCustomersPercent?: number;
    repeatCustomersPercent?: number;
  }[];
}

interface NtbOverview {
  cost: {
    cpaAll: number;
    cpaNtb: number;
    ntbCustomers: number;
    spend: number;
  };
  revenue: {
    ntbRoas: number;
    ntbSales: number;
    roasAll: number;
    spend: number;
  };
}

interface NtbOverviewGraph {
  date: string; // ISO date string
  ntbCpa: number;
  ntbRoas: number;
}

export interface GetAmcNtbResponse {
  availableFilters: AmcAvailableFilters;
  behaviorByGroup: NtbBehaviorByGroup[];
  ntbVsRepeatBehavior: NtbVsRepeatBehavior;
  overview: NtbOverview;
  overviewGraph: NtbOverviewGraph[];
}

export const sanitizeBehaviorData = (data: NtbBehaviorByGroup[]): NtbBehaviorByGroup[] => {
  return data.map(row => {
    return {
      ...row,
      customersConverted: row.customersConverted ? parseInt(String(row.customersConverted)) : 0,
      ntbCustomers: row.ntbCustomers ? parseInt(String(row.ntbCustomers)) : 0,
      ntbPurchases: row.ntbPurchases ? parseInt(String(row.ntbPurchases)) : 0,
      ntbRate: row.ntbRate ? parseFloat(String(row.ntbRate)) : 0,
      ntbRepeat: row.ntbRepeat ? parseInt(String(row.ntbRepeat)) : 0,
      ntbRepeatRate: row.ntbRepeatRate ? parseFloat(String(row.ntbRepeatRate)) : 0,
      purchases: row.purchases ? parseInt(String(row.purchases)) : 0,
    };
  });
};

export const sanitizeOverviewGraph = (data: NtbOverviewGraph[]): NtbOverviewGraph[] => {
  return data.map(row => {
    return {
      ...row,
      ntbCpa: row.ntbCpa ? parseFloat(String(row.ntbCpa)) : 0,
      ntbRoas: row.ntbRoas ? parseFloat(String(row.ntbRoas)) : 0,
    };
  });
};

export const sanitizeOverviewData = (data: NtbOverview): NtbOverview => {
  return {
    cost: {
      cpaAll: data.cost.cpaAll ? parseFloat(String(data.cost.cpaAll)) : 0,
      cpaNtb: data.cost.cpaNtb ? parseFloat(String(data.cost.cpaNtb)) : 0,
      ntbCustomers: data.cost.ntbCustomers ? parseInt(String(data.cost.ntbCustomers)) : 0,
      spend: data.cost.spend ? parseFloat(String(data.cost.spend)) : 0,
    },
    revenue: {
      ntbRoas: data.revenue.ntbRoas ? parseFloat(String(data.revenue.ntbRoas)) : 0,
      ntbSales: data.revenue.ntbSales ? parseFloat(String(data.revenue.ntbSales)) : 0,
      roasAll: data.revenue.roasAll ? parseFloat(String(data.revenue.roasAll)) : 0,
      spend: data.revenue.spend ? parseFloat(String(data.revenue.spend)) : 0,
    },
  };
};

export const sanitizeNtbVsRepeatBehavior = (data: NtbVsRepeatBehavior): NtbVsRepeatBehavior => {
  return {
    customers: data.customers.map(row => ({
      ...row,
      ntbCustomers: row.ntbCustomers ? parseInt(String(row.ntbCustomers)) : 0,
      ntbRate: row.ntbRate ? parseFloat(String(row.ntbRate)) : 0,
      ntbRepeatCustomers: row.ntbRepeatCustomers ? parseInt(String(row.ntbRepeatCustomers)) : 0,
      ntbRepeatRate: row.ntbRepeatRate ? parseFloat(String(row.ntbRepeatRate)) : 0,
      ntbCustomersPercent: row.ntbCustomersPercent
        ? parseFloat(String(row.ntbCustomersPercent))
        : 0,
      ntbRepeatCustomersPercent: row.ntbRepeatCustomersPercent
        ? parseFloat(String(row.ntbRepeatCustomersPercent))
        : 0,
    })),
    purchases: data.purchases.map(row => ({
      ...row,
      ntbCustomerRate: row.ntbCustomerRate ? parseFloat(String(row.ntbCustomerRate)) : 0,
      ntbCustomers: row.ntbCustomers ? parseInt(String(row.ntbCustomers)) : 0,
      repeatCustomerRate: row.repeatCustomerRate ? parseFloat(String(row.repeatCustomerRate)) : 0,
      repeatCustomers: row.repeatCustomers ? parseInt(String(row.repeatCustomers)) : 0,
      ntbCustomersPercent: row.ntbCustomersPercent
        ? parseFloat(String(row.ntbCustomersPercent))
        : 0,
      repeatCustomersPercent: row.repeatCustomersPercent
        ? parseFloat(String(row.repeatCustomersPercent))
        : 0,
    })),
  };
};
